// P43 — Page /profil/notifications : gestion des préférences
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { Bell, BellOff, RotateCcw, Mail } from "lucide-react";
import { useT, getLocale } from "@/lib/i18n";
import { API_BASE } from "@/lib/apiBase";
interface NotificationTypeMeta {
  type: "da_validee" | "intervention_planifiee" | "intervention_en_retard" | "stock_bas";
  label: string;
  label_en: string;
  description: string;
  description_en: string;
  default_enabled: boolean;
}

interface NotificationPreference {
  id: number;
  user_id: number;
  notification_type: string;
  channel: "email" | "in_app";
  enabled: boolean;
  updated_at: string;
}

async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options.headers },
    ...options,
  });
  if (!res.ok) {
    let errorDetail = `API ${res.status}`;
    try {
      const errBody = await res.json();
      errorDetail = errBody.detail || errorDetail;
    } catch {
      // ignore
    }
    throw new Error(errorDetail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export default function Notifications() {
  const t = useT();
  const locale = getLocale();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [resetting, setResetting] = useState(false);

  // 1. Récupérer les métadonnées des types
  const { data: types } = useQuery({
    queryKey: ["notification-types"],
    queryFn: () => fetchJson<NotificationTypeMeta[]>("/v1/notification-preferences/types"),
  });

  // 2. Récupérer les préférences courantes
  const { data: preferences, isLoading } = useQuery({
    queryKey: ["my-notification-preferences"],
    queryFn: () => fetchJson<NotificationPreference[]>("/v1/notification-preferences"),
  });

  // 3. Mutation : toggle
  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      fetchJson<NotificationPreference>(`/v1/notification-preferences/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-notification-preferences"] });
      toast({ title: t("notifications.preference_updated"), variant: "success" });
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "error" });
    },
  });

  // 4. Mutation : reset
  const resetMutation = useMutation({
    mutationFn: () =>
      fetchJson<{ reset_count: number; preferences: NotificationPreference[] }>(
        "/v1/notification-preferences/reset",
        { method: "POST" }
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["my-notification-preferences"] });
      toast({
        title: `${data.reset_count} ${t("notifications.preferences_reset")}`,
        variant: "success",
      });
      setResetting(false);
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "error" });
      setResetting(false);
    },
  });

  // Grouper les préférences par type
  const prefsByName = (preferences ?? []).reduce(
    (acc, pref) => {
      if (!acc[pref.notification_type]) acc[pref.notification_type] = [];
      acc[pref.notification_type].push(pref);
      return acc;
    },
    {} as Record<string, NotificationPreference[]>
  );

  return (
    <div className="space-y-6 p-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6" />
            {t("notifications.page_title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("notifications.page_subtitle")}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setResetting(true);
            resetMutation.mutate();
          }}
          disabled={resetting || resetMutation.isPending}
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          {t("notifications.reset_button")}
        </Button>
      </div>

      {/* Liste des types */}
      {isLoading && (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && types && (
        <div className="grid gap-4">
          {types.map((type: NotificationTypeMeta) => {
            const prefs = prefsByName[type.type] ?? [];
            const label = locale === "en" ? type.label_en : type.label;
            const description = locale === "en" ? type.description_en : type.description;

            return (
              <Card key={type.type}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{label}</span>
                    {prefs.some((p) => p.enabled) ? (
                      <Bell className="h-4 w-4 text-green-500" />
                    ) : (
                      <BellOff className="h-4 w-4 text-gray-400" />
                    )}
                  </CardTitle>
                  <CardDescription>{description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {prefs.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      {t("notifications.no_preferences_set")}
                    </p>
                  )}
                  {prefs.map((pref: NotificationPreference) => (
                    <div
                      key={pref.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4" />
                        <div>
                          <p className="text-sm font-medium">
                            {t(`notifications.channel.${pref.channel}`)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {pref.enabled
                              ? t("notifications.status.enabled")
                              : t("notifications.status.disabled")}
                          </p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={pref.enabled}
                          onChange={(e) =>
                            toggleMutation.mutate({
                              id: pref.id,
                              enabled: e.target.checked,
                            })
                          }
                          disabled={toggleMutation.isPending}
                          data-testid={`toggle-${pref.notification_type}-${pref.channel}`}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
